import { Checkbox, Group, Modal, Stack, Text } from '@mantine/core';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { useFillOutRevisionSubmitProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionSubmitProvider';
import P2Medium from 'components/typography/P2Medium';
import Button from 'components/Button';

interface SubmitRevisionModalProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Confirmation modal for submitting a revision. Optionally allows the user to
 * immediately approve and/or confirm the revision right after submission,
 * provided they have the required permissions and the template has no
 * pipeline automations on those transitions.
 */
export default function SubmitRevisionModal({ opened, onClose }: SubmitRevisionModalProps) {
  const { revision } = useFillOutRevisionDataProvider();
  const {
    submit,
    loading,
    canImmediatelyApprove,
    canImmediatelyConfirm,
    approveAfterSubmit,
    confirmAfterSubmit,
    setApproveAfterSubmit,
    setConfirmAfterSubmit,
  } = useFillOutRevisionSubmitProvider();

  const isRevision = revision.kind === 'revision';

  /** Triggers the submit action and closes the modal. */
  const handleConfirm = () => {
    submit();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isRevision ? 'Odovzdanie revíznej správy' : 'Odovzdanie formulára'}
      centered
    >
      <Stack spacing={16}>
        <Text>
          {isRevision
            ? 'Naozaj chcete odovzdať revíznu správu? Po odovzdaní ju už nebude možné upravovať.'
            : 'Naozaj chcete odovzdať formulár? Po odovzdaní ho už nebude možné upravovať.'}
        </Text>
        {(canImmediatelyApprove || canImmediatelyConfirm) && (
          <Stack spacing={8} bg="gray.0" pb={12} px={12} pt={8} sx={{ borderRadius: '4px' }}>
            <P2Medium color="gray.8">Spustiť ďalšie akcie ihneď po odovzdaní</P2Medium>
            {canImmediatelyApprove && (
              <Checkbox
                size="md"
                checked={approveAfterSubmit}
                onChange={(e) => setApproveAfterSubmit(e.currentTarget.checked)}
                label={isRevision ? 'Schváliť revíznu správu' : 'Schváliť formulár'}
              />
            )}
            {canImmediatelyConfirm && (
              <Checkbox
                size="md"
                checked={approveAfterSubmit ? confirmAfterSubmit : false}
                disabled={!approveAfterSubmit}
                onChange={(e) => setConfirmAfterSubmit(e.currentTarget.checked)}
                label={isRevision ? 'Prebrať revíznu správu' : 'Prebrať formulár'}
              />
            )}
          </Stack>
        )}
        <Group position="right" spacing={8}>
          <Button variant="subtle-gray" onClick={onClose} disabled={loading}>
            Zrušiť
          </Button>
          <Button variant="primary" onClick={handleConfirm} loading={loading} data-autofocus>
            Ukončiť a odovzdať
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
